import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { ShieldAlert, Plus, Wand2, Send, Lock } from "lucide-react";

const C = { navy: "#1C4358", slate: "#4B5563", charcoal: "#252221", line: "#E6EDF2", red: "#9C1E22", amber: "#B7791F", green: "#2F7D5A" };
const STATUS_LABEL = {
  raw_private: "Raw / private", needs_cleanup: "Needs cleanup", cleaned_draft: "Cleaned draft",
  pending_approval: "Pending approval", approved_internal: "Approved (internal)", approved_public: "Approved (public)",
  rejected: "Rejected", archived: "Archived",
};
const STATUS_TONE = {
  raw_private: C.red, needs_cleanup: C.red, cleaned_draft: C.amber, pending_approval: C.amber,
  approved_internal: C.navy, approved_public: C.green, rejected: C.slate, archived: C.slate,
};
const USE_LABEL = { internal: "Internal", public_qa: "Public Q&A", faq: "FAQ", sop_example: "SOP example", testimonial: "Testimonial" };

export default function TranscriptCleanup() {
  const [data, setData] = useState({ items: [], counts: {}, statuses: [], intended_uses: [], warning: "" });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newItem, setNewItem] = useState({ title: "", raw_text: "", intended_use: "internal" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await api.get("/security/transcript-cleanup", { params });
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [filter]);

  const openItem = async (id) => {
    try { const { data } = await api.get(`/security/transcript-cleanup/${id}`); setSel(data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const create = async () => {
    if (!newItem.raw_text.trim()) return toast.error("Paste the raw transcript text.");
    try {
      const { data } = await api.post("/security/transcript-cleanup", { ...newItem, source_type: "manual" });
      toast.success("Created — auto-redaction applied. Review before publishing.");
      setCreating(false); setNewItem({ title: "", raw_text: "", intended_use: "internal" });
      load(); openItem(data.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const patch = async (payload) => {
    try { const { data } = await api.put(`/security/transcript-cleanup/${sel.id}`, payload); setSel(data); load(); toast.success("Saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const autoRedact = async () => {
    try { const { data } = await api.post(`/security/transcript-cleanup/${sel.id}/auto-redact`); setSel(data); toast.success("Re-redacted"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const publish = async () => {
    try { const { data } = await api.post(`/security/transcript-cleanup/${sel.id}/publish`, {}); toast.success(data.message); openItem(sel.id); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const piiCount = (d) => Object.values(d?.detected_pii || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="max-w-6xl mx-auto pb-16" data-testid="transcript-cleanup-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2" style={{ color: C.charcoal }}>
          <ShieldAlert size={22} style={{ color: C.red }} /> Transcript Cleanup
        </h1>
        <Button size="sm" onClick={() => setCreating(true)} className="h-8 gap-1 bg-[#1C4358]" data-testid="tc-new-btn"><Plus size={14} /> New</Button>
      </div>
      <div className="rounded-lg border border-[#EADFBF] bg-[#FBF7EC] px-3 py-2 text-[12.5px] text-[#8a6d1f] flex items-center gap-2 my-3" data-testid="tc-warning">
        <ShieldAlert size={14} /> {data.warning || "Sensitive content must be reviewed and cleaned before publishing."}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {["all", ...(data.statuses || [])].map((s) => (
          <button key={s} data-testid={`tc-filter-${s}`} onClick={() => setFilter(s)}
            className="text-[11.5px] px-2.5 py-1 rounded-full border"
            style={{ borderColor: filter === s ? C.navy : C.line, background: filter === s ? "rgba(28,67,88,0.08)" : "#fff", color: filter === s ? C.navy : C.slate }}>
            {s === "all" ? "All" : STATUS_LABEL[s]}{data.counts?.[s] ? ` · ${data.counts[s]}` : ""}
          </button>
        ))}
      </div>

      <Card className="border-[#E6EDF2] overflow-hidden">
        {loading ? <div className="p-4 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}</div>
          : (data.items || []).length === 0 ? <div className="p-8 text-center text-[13px]" style={{ color: C.slate }} data-testid="tc-empty">No transcripts in cleanup.</div>
            : (
              <div className="divide-y" style={{ borderColor: C.line }}>
                {data.items.map((it) => (
                  <button key={it.id} data-testid="tc-row" onClick={() => openItem(it.id)} className="w-full text-left px-4 py-3 hover:bg-[#F7FAFC]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13.5px] font-medium" style={{ color: C.charcoal }}>{it.title}</span>
                      <span className="text-[10.5px] px-1.5 py-0.5 rounded-full" style={{ background: `${STATUS_TONE[it.status]}1A`, color: STATUS_TONE[it.status] }}>{STATUS_LABEL[it.status]}</span>
                    </div>
                    <div className="text-[11.5px] mt-0.5" style={{ color: C.slate }}>
                      {USE_LABEL[it.intended_use]} · {piiCount(it) ? `${piiCount(it)} PII flag(s)` : "no PII flagged"} · {formatDateTime(it.updated_at)}
                    </div>
                  </button>
                ))}
              </div>
            )}
      </Card>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New transcript cleanup</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title" value={newItem.title} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} data-testid="tc-new-title" />
            <Textarea placeholder="Paste the raw transcript / chat / intake text here" rows={7} value={newItem.raw_text}
              onChange={(e) => setNewItem({ ...newItem, raw_text: e.target.value })} data-testid="tc-new-raw" />
            <Select value={newItem.intended_use} onValueChange={(v) => setNewItem({ ...newItem, intended_use: v })}>
              <SelectTrigger data-testid="tc-new-use"><SelectValue /></SelectTrigger>
              <SelectContent>{(data.intended_uses || []).map((u) => <SelectItem key={u} value={u}>{USE_LABEL[u]}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="w-full bg-[#1C4358]" onClick={create} data-testid="tc-create-submit">Create & auto-redact</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="tc-detail">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2">{sel.title}
                <span className="text-[10.5px] px-1.5 py-0.5 rounded-full" style={{ background: `${STATUS_TONE[sel.status]}1A`, color: STATUS_TONE[sel.status] }}>{STATUS_LABEL[sel.status]}</span>
              </DialogTitle></DialogHeader>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] font-semibold mb-1 flex items-center gap-1" style={{ color: C.red }}><Lock size={11} /> Raw (private)</div>
                  <div className="text-[12px] rounded-lg border p-2 whitespace-pre-wrap max-h-56 overflow-y-auto bg-[#FCF6F6]" style={{ borderColor: C.line, color: C.charcoal }} data-testid="tc-raw">{sel.raw_text}</div>
                  {piiCount(sel) > 0 && <div className="text-[11px] mt-1" style={{ color: C.red }}>{piiCount(sel)} personal identifier(s) detected: {Object.keys(sel.detected_pii || {}).join(", ")}</div>}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[11px] font-semibold flex items-center gap-1" style={{ color: C.green }}>Cleaned (publishable)</div>
                    <button onClick={autoRedact} className="text-[11px] inline-flex items-center gap-1" style={{ color: C.navy }} data-testid="tc-autoredact"><Wand2 size={11} /> Auto-redact</button>
                  </div>
                  <Textarea value={sel.cleaned_text || ""} onChange={(e) => setSel({ ...sel, cleaned_text: e.target.value })} rows={9} className="text-[12px]" data-testid="tc-cleaned" />
                  <Button size="sm" variant="outline" className="mt-1.5 h-7 text-[12px]" onClick={() => patch({ cleaned_text: sel.cleaned_text })} data-testid="tc-save-cleaned">Save cleaned text</Button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: C.line }}>
                <span className="text-[12px]" style={{ color: C.slate }}>Status:</span>
                <Select value={sel.status} onValueChange={(v) => patch({ status: v })}>
                  <SelectTrigger className="h-8 w-[190px] text-[12.5px]" data-testid="tc-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{(data.statuses || []).map((s) => <SelectItem key={s} value={s} data-testid={`tc-status-${s}`}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" className="h-8 gap-1 bg-[#2F7D5A] disabled:opacity-50" disabled={sel.status !== "approved_public"} onClick={publish} data-testid="tc-publish-btn">
                  <Send size={13} /> Publish to Q&A
                </Button>
                {sel.published_qa_id && <span className="text-[11.5px]" style={{ color: C.green }}>Sent to Q&A queue (draft)</span>}
              </div>
              <p className="text-[11px] mt-1" style={{ color: C.slate }}>Publishing requires "Approved (public)" status and no detectable personal information. Content lands in the Q&A queue as a draft — never auto-live.</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
